import { useMemo } from 'react';
import { LendingPair } from '../data/LendingPair';
import { useQuery } from '@tanstack/react-query';
import axios from 'axios';

const PYTH_API_ENDPOINTS = {
  LATEST: 'https://hermes.pyth.network/v2/updates/price/latest',
  UPDATES: 'https://hermes.pyth.network/v2/updates/price'
};
interface PythPrice {
  price: string;
  conf: string;
  expo: number;
  publish_time: number;
}

interface PythPriceResponse {
  id: string;
  price: {
    price: string;
    conf: string;
    expo: number;
    publish_time: number;
  };
  ema_price: {
    price: string;
    conf: string;
    expo: number;
    publish_time: number;
  };
}

interface PythApiResponse {
  binary: {
    data: string[];
    encoding: 'hex' | 'base64';
  };
  parsed: PythPriceResponse[];
}

type TokenSymbol = string;
type Quote = number;

export type PriceRelayLatestResponse = {
  [key: TokenSymbol]: {
    price: Quote;
  };
};

export type PriceRelayHistoricalResponse = {
  [key: TokenSymbol]: {
    prices: {
      price: Quote;
      timestamp: number;
    }[];
  };
};

export type PriceRelayConsolidatedResponse = {
  latest: PriceRelayLatestResponse;
  historical: PriceRelayHistoricalResponse;
};

// Mapping of your tokens to Pyth price feed IDs
const PRICE_FEED_IDS: Record<string, string> = {
  'WBERA': '962088abcfdbdb6e30db2e340c8cf887d9efb311b1f2f17b155a63dbb6d40265',
  'HONEY': 'f67b033925d73d43ba4401e00308d9b0f26ab4fbd1250e8b5407b9eaade7e1f4',
  // Add other tokens as needed
};

function getPythPriceIdForSymbol(symbol: string): string {
  return PRICE_FEED_IDS[symbol.toUpperCase()] || '';
}

function convertPythPrice(priceStr: string, expo: number): number {
  return Number(priceStr) * Math.pow(10, expo);
}

/// Compute unique tokens, sorted alphabetically
function commaSeparatedSymbolsFor(lendingPairs: LendingPair[]) {
  const symbolSet = new Set<string>();
  lendingPairs.forEach((pair) => {
    symbolSet.add(pair.token0.symbol);
    symbolSet.add(pair.token1.symbol);
  });
  const symbols = Array.from(symbolSet);
  symbols.sort((a, b) => a.localeCompare(b));
  return symbols.join(',').toUpperCase();
}

function toLatestPrices(response: PriceRelayLatestResponse) {
  const latestPrices = new Map<TokenSymbol, Quote>();
  Object.entries(response).forEach(([k, v]) => {
    latestPrices.set(k, v.price);
    latestPrices.set(k.toLowerCase(), v.price);
  });
  return latestPrices;  // Move return statement outside of forEach
}

function toHistoricalPrices(response: PriceRelayHistoricalResponse) {
  const historicalPrices = new Map<TokenSymbol, { price: Quote; timestamp: number }[]>();
  Object.entries(response).forEach(([k, v]) => {
    historicalPrices.set(k, v.prices);
    historicalPrices.set(k.toLowerCase(), v.prices);
  });
  return historicalPrices;
}

export function useLatestPriceRelay(lendingPairs: LendingPair[], refetchInterval = 60 * 1_000) {
  const priceIds = useMemo(() => {
    const symbolSet = new Set<string>();
    lendingPairs.forEach((pair) => {
      symbolSet.add(pair.token0.symbol);
      symbolSet.add(pair.token1.symbol);
    });
    
    return Array.from(symbolSet)
      .map(symbol => ({
        symbol,
        priceId: getPythPriceIdForSymbol(symbol)
      }))
      .filter(({ priceId }) => priceId);
  }, [lendingPairs]);

  const queryFn = async () => {
    const latestPrices = new Map<TokenSymbol, Quote>();
    
    if (priceIds.length === 0) {
      return latestPrices;
    }

    try {
      const params = new URLSearchParams();
      priceIds.forEach(({ priceId }) => {
        params.append('ids[]', priceId);
      });
      
      params.append('parsed', 'true');
      params.append('ignore_invalid_price_ids', 'true');
      
      // Use the /latest endpoint instead of a specific timestamp
      const response = await axios.get<PythApiResponse>(
        `${PYTH_API_ENDPOINTS.LATEST}?${params.toString()}`
      );
      
      if (!response.data?.parsed) {
        throw new Error('Invalid response format from Pyth API');
      }

      response.data.parsed.forEach((priceData) => {
        const matchingPair = priceIds.find(({ priceId }) => priceId === priceData.id);
        if (!matchingPair) return;

        const { symbol } = matchingPair;
        const price = convertPythPrice(priceData.price.price, priceData.price.expo);
        
        latestPrices.set(symbol, price);
        latestPrices.set(symbol.toLowerCase(), price);
      });

    } catch (error) {
      console.error('Failed to fetch Pyth prices:', error);
      throw new Error('Price relay failed to respond.');
    }
    
    return latestPrices;
  };

  return useQuery({
    queryKey: ['usePythPriceRelay', 'latest', priceIds.map(p => p.priceId).join(',')],
    queryFn,
    staleTime: refetchInterval,
    refetchOnMount: true,
    refetchOnWindowFocus: true,
    refetchOnReconnect: true,
    refetchInterval,
    refetchIntervalInBackground: false,
    placeholderData: new Map<TokenSymbol, Quote>(),
    enabled: priceIds.length > 0,
  });
}

export function useHistoricalPriceRelay(lendingPairs: LendingPair[], refetchInterval = 20 * 60 * 1_000) {
  const priceIds = useMemo(() => {
    const symbolSet = new Set<string>();
    lendingPairs.forEach((pair) => {
      symbolSet.add(pair.token0.symbol);
      symbolSet.add(pair.token1.symbol);
    });
    
    return Array.from(symbolSet)
      .map(symbol => ({
        symbol,
        priceId: getPythPriceIdForSymbol(symbol)
      }))
      .filter(({ priceId }) => priceId);
  }, [lendingPairs]);

  const queryFn = async () => {
    const historicalPrices = new Map<TokenSymbol, { price: Quote; timestamp: number }[]>();
    
    if (priceIds.length === 0) {
      return historicalPrices;
    }

    try {
      const params = new URLSearchParams();
      priceIds.forEach(({ priceId }) => {
        params.append('ids[]', priceId);
      });
      
      params.append('parsed', 'true');
      params.append('ignore_invalid_price_ids', 'true');

      // First get the latest prices to get a valid timestamp
      const latestResponse = await axios.get<PythApiResponse>(
        `${PYTH_API_ENDPOINTS.LATEST}?${params.toString()}`
      );

      if (!latestResponse.data?.parsed || latestResponse.data.parsed.length === 0) {
        throw new Error('No latest price data available');
      }

      // Use the latest price's publish time as reference
      const latestTimestamp = latestResponse.data.parsed[0].price.publish_time;
      const hourInSeconds = 3600;

      // Calculate timestamps for historical data (last 24 hours)
      const timestamps = Array.from({ length: 24 }, (_, i) => 
        latestTimestamp - (i * hourInSeconds)
      );

      // Initialize price arrays for each symbol
      priceIds.forEach(({ symbol }) => {
        historicalPrices.set(symbol, []);
        historicalPrices.set(symbol.toLowerCase(), []);
      });

      // Process latest prices first
      latestResponse.data.parsed.forEach((priceData) => {
        const matchingPair = priceIds.find(({ priceId }) => priceId === priceData.id);
        if (!matchingPair) return;

        const { symbol } = matchingPair;
        const price = convertPythPrice(priceData.price.price, priceData.price.expo);
        const timestamp = priceData.price.publish_time;

        const priceEntry = { price, timestamp };
        historicalPrices.set(symbol, [priceEntry]);
        historicalPrices.set(symbol.toLowerCase(), [priceEntry]);
      });

      // Fetch historical prices
      for (const timestamp of timestamps.slice(1)) { // Skip first timestamp as we already have it
        try {
          const response = await axios.get<PythApiResponse>(
            `${PYTH_API_ENDPOINTS.UPDATES}/${timestamp}?${params.toString()}`
          );

          if (response.data?.parsed) {
            response.data.parsed.forEach((priceData) => {
              const matchingPair = priceIds.find(({ priceId }) => priceId === priceData.id);
              if (!matchingPair) return;

              const { symbol } = matchingPair;
              const price = convertPythPrice(priceData.price.price, priceData.price.expo);
              const priceTimestamp = priceData.price.publish_time;

              const priceEntry = { price, timestamp: priceTimestamp };
              const existingPrices = historicalPrices.get(symbol) || [];
              historicalPrices.set(symbol, [...existingPrices, priceEntry]);
              historicalPrices.set(symbol.toLowerCase(), [...existingPrices, priceEntry]);
            });
          }
        } catch (error) {
          console.warn(`Failed to fetch historical price for timestamp ${timestamp}, continuing...`);
          continue;
        }
      }

      // Sort and deduplicate prices for each symbol
      priceIds.forEach(({ symbol }) => {
        const prices = historicalPrices.get(symbol) || [];
        const sortedPrices = prices
          .sort((a, b) => b.timestamp - a.timestamp)
          .filter((price, index, self) => 
            index === self.findIndex(p => p.timestamp === price.timestamp)
          );
        
        historicalPrices.set(symbol, sortedPrices);
        historicalPrices.set(symbol.toLowerCase(), sortedPrices);
      });

    } catch (error) {
      console.error('Failed to fetch Pyth historical prices:', error);
      throw new Error('Price relay failed to respond.');
    }
    
    return historicalPrices;
  };

  return useQuery({
    queryKey: ['usePythPriceRelay', 'historical', priceIds.map(p => p.priceId).join(',')],
    queryFn,
    staleTime: refetchInterval,
    refetchOnMount: true,
    refetchOnWindowFocus: true,
    refetchOnReconnect: true,
    refetchInterval,
    refetchIntervalInBackground: false,
    placeholderData: new Map<TokenSymbol, { price: Quote; timestamp: number }[]>(),
    enabled: priceIds.length > 0,
  });
}

export function useConsolidatedPriceRelay(lendingPairs: LendingPair[], refetchInterval = 60 * 1_000) {
  const latestPricesQuery = useLatestPriceRelay(lendingPairs, refetchInterval);
  const historicalPricesQuery = useHistoricalPriceRelay(lendingPairs, refetchInterval);

  return useQuery({
    queryKey: ['usePythPriceRelay', 'consolidated', lendingPairs],
    queryFn: async () => ({
      latestPrices: await latestPricesQuery.refetch().then(result => result.data || new Map()),
      historicalPrices: await historicalPricesQuery.refetch().then(result => result.data || new Map()),
    }),
    staleTime: refetchInterval,
    refetchOnMount: true,
    refetchOnWindowFocus: true,
    refetchOnReconnect: true,
    refetchInterval,
    refetchIntervalInBackground: false,
    placeholderData: {
      latestPrices: new Map<TokenSymbol, Quote>(),
      historicalPrices: new Map<TokenSymbol, { price: Quote; timestamp: number }[]>(),
    },
    enabled: lendingPairs.length > 0,
  });
}

// export function useHistoricalPriceRelay(lendingPairs: LendingPair[], refetchInterval = 20 * 60 * 1_000) {
//   const commaSeparatedSymbols = useMemo(() => commaSeparatedSymbolsFor(lendingPairs), [lendingPairs]);

//   const queryFn = async () => {
//     const response = (await axios.get(`${API_PRICE_RELAY_HISTORICAL_URL}?symbols=${commaSeparatedSymbols}`)).data;
//     if (!response) {
//       throw new Error('Price relay failed to respond.');
//     }
//     return toHistoricalPrices(response);
//   };

//   const queryKey = ['usePriceRelay', 'historical', commaSeparatedSymbols];

//   return useQuery({
//     queryKey,
//     queryFn,
//     staleTime: refetchInterval,
//     refetchOnMount: true,
//     refetchOnWindowFocus: true,
//     refetchOnReconnect: true,
//     refetchInterval,
//     refetchIntervalInBackground: false,
//     placeholderData: new Map<TokenSymbol, { price: Quote; timestamp: number }[]>(),
//     enabled: lendingPairs.length > 0,
//   });
// }

// export function useConsolidatedPriceRelay(lendingPairs: LendingPair[], refetchInterval = 60 * 1_000) {
//   const commaSeparatedSymbols = useMemo(() => commaSeparatedSymbolsFor(lendingPairs), [lendingPairs]);

//   const queryFn = async () => {
//     const response = (await axios.get(`${API_PRICE_RELAY_CONSOLIDATED_URL}?symbols=${commaSeparatedSymbols}`)).data;
//     if (!response) {
//       throw new Error('Price relay failed to respond.');
//     }
//     return {
//       latestPrices: toLatestPrices(response.latest),
//       historicalPrices: toHistoricalPrices(response.historical),
//     };
//   };

//   const queryKey = ['usePriceRelay', 'consolidated', commaSeparatedSymbols];

//   return useQuery({
//     queryKey,
//     queryFn,
//     staleTime: refetchInterval,
//     refetchOnMount: true,
//     refetchOnWindowFocus: true,
//     refetchOnReconnect: true,
//     refetchInterval,
//     refetchIntervalInBackground: false,
//     placeholderData: {
//       latestPrices: new Map<TokenSymbol, Quote>(),
//       historicalPrices: new Map<TokenSymbol, { price: Quote; timestamp: number }[]>(),
//     },
//     enabled: lendingPairs.length > 0,
//   });
// }